import { z } from 'zod';
import { TxType } from './tx-type';

/**
 * 알림 계약 (10회차 결정 1).
 *
 * 규칙은 코드가 아니라 파일(`alerts.yml`)에 둔다. 임계값은 운영하면서 계속 바뀌는 값이라
 * 배포 없이 고칠 수 있어야 한다. 대신 파일이 곧 입력이므로 모양은 여기서 못 박는다.
 *
 * 엔진은 이미 서버에 흐르는 신호만 본다. 새로 재는 것은 없다.
 *   - 스냅샷 게이지(`loop_p99`·`heap_used_pct`·`pool_waiting`) — 1초마다 오는 `Snapshot`
 *   - 창 집계(`tx_error_rate`·`tx_p99`·`sql_error`)          — 끝난 요청 피드
 *   - DB 상태(`db_blocked`·`db_idle_in_tx`·`db_long_query`)   — 워처의 `DbSnapshot`
 *   - `profile_captured`                                     — 프로파일 저장 훅(정보성)
 *   - `instance_gone`·`instance_restarted`                   — 생존 판정의 사건(통보, 53회차)
 *   - `server_error`                                         — 관제 서버 자신의 ERROR 로그(통보, 53회차)
 *
 * 53회차 셋은 `profile_captured`와 같은 모양이다. 조건이 없고 그 자리에서 닫히는 통보이며,
 * 잴 값이 없어 `value: 1`·`threshold: 0`이고 뜻은 `message`가 싣는다.
 */

const nonneg = z.number().nonnegative();
const count = z.number().int().nonnegative();

/** 신호 종류. 규칙 하나는 신호 하나를 본다 */
export const ALERT_SIGNALS = [
  'loop_p99',
  'heap_used_pct',
  'pool_waiting',
  'tx_error_rate',
  'tx_p99',
  'sql_error',
  'db_blocked',
  'db_idle_in_tx',
  'db_long_query',
  'profile_captured',
  'instance_gone',
  'instance_restarted',
  'server_error',
] as const;
export const AlertSignal = z.enum(ALERT_SIGNALS);
export type AlertSignal = z.infer<typeof AlertSignal>;

export const AlertSeverity = z.enum(['warning', 'critical']);
export type AlertSeverity = z.infer<typeof AlertSeverity>;

/**
 * 알림 한 건의 상태 (24회차, 감사 D-23).
 *
 *   `firing`   조건에 걸려 경보 중이다. 아직 안 풀렸다
 *   `resolved` 조건이 풀려 끝났다
 *   `notice`   그 순간의 통보다. 발화로 남지 않고 그 자리에서 끝난다
 *              (CPU 프로파일 수집·알림 폭주 억제)
 *
 * `notice`가 없던 때는 통보도 `firing`으로 저장돼 `GET /api/alerts?state=firing`이
 * "지금 경보 중"과 다른 수를 냈다. 화면은 `endedAt`으로 빼고 있었지만 조회는 그러지 않았다.
 */
export const AlertState = z.enum(['firing', 'resolved', 'notice']);
export type AlertState = z.infer<typeof AlertState>;

/**
 * 임계값. 신호마다 쓰는 칸이 다르다(`loop_p99`는 `gtMs`, `tx_error_rate`는 `gtPct`+`minCount`).
 * 한 스키마에 다 담고 규칙 검증에서 신호별로 필요한 칸이 있는지 본다.
 * 신호마다 스키마를 나누면 YAML 한 줄 오타에 "어느 모양도 아니다"라는 쓸모없는 오류만 남는다.
 *
 * **모르는 칸은 오류다**(`strictObject`, 24회차 리뷰 12). 전에는 `cooldonMs`처럼 오타가 난
 * 칸을 조용히 버려서, 적은 사람은 10분 쿨다운을 걸었다고 믿는데 실제로는 기본값으로 돌았다.
 * 규칙 파일은 오타가 곧 사고다.
 */
export const AlertCondition = z.strictObject({
  /** ms 임계값. `loop_p99`·`tx_p99` */
  gtMs: nonneg.optional(),
  /**
   * % 임계값(0~100). `heap_used_pct`·`tx_error_rate`. 100을 넘는 값은 영영 안 울리는 규칙이라
   * 그 규칙만 버리고 까닭을 `problems`에 남긴다 (49회차, D45-5)
   */
  gtPct: nonneg.max(100).optional(),
  /** 단위 없는 게이지 임계값. `pool_waiting` */
  gt: nonneg.optional(),
  /** 표본이 이만큼은 돼야 판정한다. 요청 3건 중 1건 실패로 33%라고 알리지 않기 위해서다 */
  minCount: count.optional(),
  /** DB 신호의 지속 시간 임계값 */
  minDurationMs: nonneg.optional(),
});
export type AlertCondition = z.infer<typeof AlertCondition>;

/**
 * 이 규칙이 볼 대상 (26회차).
 *
 * `keyBy`가 "무엇으로 쪼갤지"라면 이쪽은 "무엇만 볼지"다. 앱마다 임계값이 다를 때 쓴다 —
 * 예를 들어 PLM에는 3초, OneWorks2에는 1초 규칙을 따로 걸 수 있다. 적지 않으면 전부 본다.
 *
 * 26회차 전에는 규칙에 대상 조건이 없어 서비스가 둘이 되는 순간 임계값을 하나로 맞출 수밖에
 * 없었다. `keyBy: service`로 쪼개도 임계값은 규칙 하나의 값이라 나뉘지 않는다.
 */
export const AlertMatch = z.strictObject({
  /** 이 `service.name`들만 본다. 하나라도 적으면 나머지 서비스는 이 규칙에 안 걸린다 */
  service: z.array(z.string().min(1)).min(1).max(32).optional(),
  /** 이 트랜잭션 타입만 본다. 창 집계 신호(`tx_error_rate`·`tx_p99`·`sql_error`)에만 쓴다 */
  txType: z.array(TxType).min(1).max(8).optional(),
});
export type AlertMatch = z.infer<typeof AlertMatch>;

/**
 * 상태를 쪼갤 기준. 문자열 하나이거나 목록이다 (26회차).
 *
 * 목록이면 각 부분의 값을 공백으로 이어 키를 만든다(`[service, txType]` → `plm http`).
 * 전에는 하나뿐이라 `tx_p99`를 타입별로 보면 서비스가 섞이고, 서비스별로 보면 타입이 섞였다.
 * `instance` 부분은 인스턴스 id만으로는 서비스가 구분되지 않아 언제나 `서비스 인스턴스`다 —
 * 그래서 `service`와 함께 적을 수 없다.
 */
export const AlertKeyBy = z.union([
  z.string().min(1).max(32),
  z.array(z.string().min(1).max(32)).min(1).max(3),
]);
export type AlertKeyBy = z.infer<typeof AlertKeyBy>;

/**
 * 규칙 한 줄.
 *
 * `for`는 "연속으로 이만큼 넘어야 발화"(초), `window`는 "이 구간의 집계를 본다"(초)다.
 * 둘은 성격이 다르다. 게이지는 `for`, 창 집계는 `window`를 쓴다.
 */
export const AlertRule = z.strictObject({
  /** 파일 안에서 유일해야 한다. 이벤트 id와 상태 키에 그대로 들어간다 */
  id: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[A-Za-z0-9._-]+$/),
  name: z.string().min(1).max(120),
  severity: AlertSeverity,
  signal: AlertSignal,
  condition: AlertCondition,
  /** 창 집계 구간(초). 기본 60 */
  window: z.number().positive().max(3600).optional(),
  /** 연속 초과 지속 시간(초). 없으면 한 번만 넘어도 발화 */
  for: z.number().nonnegative().max(3600).optional(),
  /**
   * 풀린 상태가 연속으로 이만큼 이어져야 복구로 닫는다(초) (49회차, D43-4). 해소 직후 다시 넘는
   * 깜빡임을 막는다. 그 사이 다시 넘으면 같은 발화가 이어진다. 없으면 0이라 지금처럼 풀린 즉시
   * 복구다. 값이 끊기거나 트래픽이 멎어 닫는 경우에는 걸지 않는다
   */
  resolveFor: z.number().nonnegative().max(3600).optional(),
  /** 같은 규칙×키를 다시 알리기까지의 최소 간격. 기본 10분 */
  cooldownMs: count.default(600_000),
  /** 상태를 무엇으로 쪼갤지. 신호마다 쓸 수 있는 값이 다르다. 목록이면 이어 붙인다 (26회차) */
  keyBy: AlertKeyBy.optional(),
  /** 이 규칙이 볼 대상만 한정한다 (26회차). 적지 않으면 전부 본다 */
  match: AlertMatch.optional(),
  /** 채널 이름 목록. 파일의 `channels:` 절에 있어야 한다 */
  channels: z.array(z.string().min(1)).min(1),
  /** 조건이 풀렸을 때 복구 알림을 보낼지 */
  notifyOnResolve: z.boolean().default(true),
  /**
   * 창 집계에 sse 트랜잭션을 넣을지 (2026-09-07 결정 4).
   *
   * sse 연결은 수십 분 유지되므로 `tx_p99`에 섞이면 그 값이 곧 연결 시간이 된다. 기본은
   * 제외하고, sse만 따로 보고 싶은 규칙에서 켠다(`keyBy: txType`으로 나눠 보는 것과는 다르다).
   * 창 집계 신호(`tx_error_rate`·`tx_p99`·`sql_error`)에만 뜻이 있다.
   */
  includeSse: z.boolean().default(false),
  /** 끄고 싶을 때 지우지 않고 false로 둔다 */
  enabled: z.boolean().default(true),
});
export type AlertRule = z.infer<typeof AlertRule>;

/**
 * 발송 채널 정의.
 *
 * 웹훅 주소는 **파일에 두지 않는다**. 채널 이름에서 유도한 환경변수
 * `APM_ALERT_WEBHOOK_<이름 대문자>`에서만 읽는다. 규칙 파일은 배포물에 그대로 들어가므로
 * 비밀이 남으면 안 된다(규칙 4의 태도와 같다). `oneworks-knowledge`의 토큰도 같은 이유로
 * 파일에 두지 않는다.
 */
export const AlertChannel = z.strictObject({
  name: z
    .string()
    .min(1)
    .max(32)
    .regex(/^[A-Za-z0-9_-]+$/),
  /**
   * 어디로 보내는지. `oneworks-knowledge`는 대상 앱의 지식 문서로 발행한다 (16회차 B안).
   * 그 채널의 주소·토큰·회사·카테고리는 파일이 아니라 `APM_ONEWORKS_*`에서 읽는다.
   *
   * `mail`·`telegram`(53회차)은 **누구에게**를 담당자 저장소(관리 → 외부 연동)에서 고른다.
   * 보내는 쪽 자격(SMTP 계정·비밀번호, 봇 토큰)은 `APM_MAIL_*`·`APM_TELEGRAM_BOT_TOKEN`에만 있다.
   */
  type: z.enum(['webhook', 'log', 'console', 'oneworks-knowledge', 'mail', 'telegram']),
  /** 웹훅 본문 모양. `json`은 `AlertEvent` 그대로. 웹훅에서만 뜻이 있다 */
  format: z.enum(['json', 'slack', 'teams']).default('json'),
  timeoutMs: z.number().int().positive().max(60_000).default(5_000),
});
export type AlertChannel = z.infer<typeof AlertChannel>;

/** 규칙 파일 전체. `channels:`와 `rules:` 두 절뿐이다 */
export const AlertRulesFile = z.strictObject({
  channels: z.array(AlertChannel).default([]),
  rules: z.array(AlertRule).default([]),
});
export type AlertRulesFile = z.infer<typeof AlertRulesFile>;

/** 알림에서 화면으로 건너가는 길. 있는 것만 싣는다 */
export const AlertLinks = z.object({
  /** 그 알림을 만든 요청. 누르면 요청 상세로 간다 */
  traceId: z
    .string()
    .regex(/^[0-9a-f]{32}$/)
    .optional(),
  /** `service instance`가 아니라 사람이 읽는 인스턴스 이름 */
  instance: z.string().optional(),
  /** 함께 뜬 CPU 프로파일 */
  profileId: z.string().optional(),
  /** 검색 화면에 그대로 넘길 쿼리스트링(`?`는 뺀다) */
  search: z.string().optional(),
});
export type AlertLinks = z.infer<typeof AlertLinks>;

/**
 * 알림 한 건. 발화와 복구가 같은 `id`를 쓴다.
 *
 * 같은 id로 두 줄(발화·복구)이 저장되므로 조회는 id로 합쳐 나중 것을 남긴다.
 * 사건 하나가 목록에서 두 줄로 보이면 "몇 번 났는지"를 셀 수 없다.
 */
export const AlertEvent = z.object({
  /** `{ruleId}-{key}-{startedAt}` */
  id: z.string().min(1),
  ruleId: z.string().min(1),
  ruleName: z.string().min(1),
  severity: AlertSeverity,
  /**
   * 어떤 신호인지. 화면이 값에 붙일 단위를 여기서 고른다.
   * 규칙이 만든 알림에는 항상 있다. 없는 것은 엔진 자신이 낸 알림(폭주 억제)뿐이다.
   */
  signal: AlertSignal.optional(),
  /** `keyBy`로 쪼갠 값. 안 쪼개면 `all` */
  key: z.string().min(1),
  /**
   * 이 알림이 가리키는 서비스 (26회차). **키가 서비스를 못 박을 때만** 실린다 —
   * `keyBy`에 `service`나 `instance`가 있거나, `match.service`가 하나뿐인 규칙이다.
   * `keyBy: all`처럼 여러 서비스를 한 값으로 묶는 규칙과 DB·엔진 자신의 알림에는 없다.
   * 저장 폴더와 `?service=` 필터가 이 값을 본다. 26회차 전 파일에는 없어 NULL로 온다.
   * **빈 문자열·공백만 있는 값은 없는 것과 같다** (30회차). 서버가 읽을 때 `undefined`로 정규화해
   * 내보내고, 조회 조건·저장 폴더도 같은 기준으로 판정한다(`alert/scope.ts`)
   */
  service: z.string().optional(),
  /** 이 알림이 가리키는 인스턴스. `keyBy`에 `instance`가 있을 때만 실린다 (26회차) */
  instance: z.string().optional(),
  state: AlertState,
  /** 발화 시점(복구면 마지막)에 관측한 값 */
  value: z.number(),
  /** 그때 적용된 임계값 */
  threshold: z.number(),
  startedAt: z.number().int().nonnegative(),
  /** 복구 시각. 발화 줄에는 없다 */
  endedAt: z.number().int().nonnegative().optional(),
  /** 화면·채널이 그대로 쓰는 한글 한 줄 */
  message: z.string().min(1),
  links: AlertLinks.default({}),
  /**
   * 이 전이를 외부 채널(`log`·`webhook`·`oneworks-knowledge`)로 **보내기로 했는지**
   * (24회차, 감사 D-21).
   *
   * 쿨다운·분당 상한은 **발송에만** 건다. 상태가 바뀐 사실은 억제됐든 아니든 이력과 화면에
   * 그대로 남는다. 그러지 않으면 `/api/alerts/active`에는 있는데 이력·SSE에는 없는 알림이
   * 생기고, 그 복구도 안 나가 화면의 카드가 다음 재연결까지 지워지지 않는다.
   *
   * **뜻은 여기까지다.** 두 억제를 다 통과했다는 것이지 채널이 받았다는 뜻이 아니다
   * (24회차 리뷰 11). 웹훅 주소가 비었거나 전송이 실패해도 이 값은 `true`다 — 발송은
   * 응답을 기다리지 않고, 알림 한 건이 채널 넷으로 갈라지면 결과도 넷이라 한 칸에 담기지
   * 않는다. 실제로 닿았는지는 `DispatchStats.failed`와 채널별 카운터가 말한다.
   *
   * 값은 쿨다운과 분당 상한을 **둘 다 본 뒤 한 번만** 정해진다. 그래서 같은 알림 id면
   * `active`·SSE·이력·외부 API가 같은 값을 낸다 (24회차 리뷰 1).
   *
   * 이 칸이 없는 옛 파일은 `true`로 읽는다. 그때는 나간 것만 저장됐기 때문이다.
   */
  notified: z.boolean().default(true),
});
export type AlertEvent = z.infer<typeof AlertEvent>;

/** 화면 문구. 서버 진단과 콘솔이 같은 이름을 쓴다 */
export const ALERT_SIGNAL_LABEL: Record<AlertSignal, string> = {
  loop_p99: '이벤트루프 p99',
  heap_used_pct: '힙 사용률',
  pool_waiting: '커넥션 풀 대기',
  tx_error_rate: '오류율',
  tx_p99: '응답시간 p99',
  sql_error: 'SQL 오류 요청',
  db_blocked: 'DB 막힘',
  db_idle_in_tx: '유휴 트랜잭션',
  db_long_query: '긴 쿼리',
  profile_captured: 'CPU 프로파일 수집',
  instance_gone: '인스턴스 사라짐',
  instance_restarted: '인스턴스 재기동',
  server_error: '관제 서버 오류',
};

export const ALERT_SEVERITY_LABEL: Record<AlertSeverity, string> = {
  warning: '경고',
  critical: '심각',
};

/** 값에 붙는 단위. 메시지와 화면이 같은 기준을 쓴다 */
export const ALERT_SIGNAL_UNIT: Record<AlertSignal, 'ms' | 'pct' | 'count'> = {
  loop_p99: 'ms',
  heap_used_pct: 'pct',
  pool_waiting: 'count',
  tx_error_rate: 'pct',
  tx_p99: 'ms',
  sql_error: 'count',
  db_blocked: 'ms',
  db_idle_in_tx: 'ms',
  db_long_query: 'ms',
  profile_captured: 'ms',
  instance_gone: 'count',
  instance_restarted: 'count',
  server_error: 'count',
};
